import { useState } from "react";
import { MessageCircle, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { LINKS, whatsappWith, CTA_IMAGE, COPY } from "@/lib/site";
import { Reveal } from "./Motion";
import { Img } from "./Img";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", brand: "", about: "", contact: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const message =
    `Hi Sona! I'd like to start a collaboration.\n\n` +
    `Name: ${form.name || "—"}\n` +
    `Brand / Business: ${form.brand || "—"}\n` +
    `Collaboration: ${form.about || "—"}\n` +
    `Reach me on: ${form.contact || "—"}`;

  const startConversation = () => {
    window.open(whatsappWith(message), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ink text-beige">
      {/* dramatic image band */}
      <div className="relative h-[42vh] md:h-[56vh] overflow-hidden">
        <Img
          name={CTA_IMAGE}
          alt="Sona exploring a palm-lined coastal road in Odisha"
          position="center 22%"
          imgClassName="w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-editorial pb-10 md:pb-14 w-full">
            <Reveal>
              <p className="kicker !text-beige/70 mb-4">{COPY.contact.kicker}</p>
              <h2 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.92] tracking-tight max-w-4xl">
                {COPY.contact.heading}
              </h2>
            </Reveal>
          </div>
        </div>
      </div>

      <div className="container-editorial py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-lg md:text-2xl font-light font-display italic text-beige/85 leading-snug max-w-md">
                {COPY.contact.sub}
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-10 flex flex-col gap-4 max-w-sm">
              <a
                data-testid="contact-cta-whatsapp"
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary !bg-beige !text-ink w-full"
              >
                <MessageCircle size={17} /> {COPY.contact.ctaWhatsapp}
              </a>
              <a
                data-testid="contact-cta-email"
                href={LINKS.email}
                className="btn-ghost !border-beige !text-beige hover:!bg-beige hover:!text-ink w-full"
              >
                <Mail size={17} /> {COPY.contact.ctaEmail}
              </a>
            </Reveal>

            <Reveal delay={0.16} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
              <a
                data-testid="contact-instagram"
                href={LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-beige/70 hover:text-beige transition-colors"
              >
                <Instagram size={16} /> {LINKS.instagramHandle}
              </a>
              <a
                href={LINKS.email}
                className="inline-flex items-center gap-2 min-w-0 break-all text-beige/70 hover:text-beige transition-colors"
              >
                <Mail size={16} className="shrink-0" /> {LINKS.emailPlain}
              </a>
            </Reveal>
          </div>

          {/* Optional short form */}
          <div className="lg:col-span-7">
            <Reveal className="border border-beige/20 rounded-sm p-6 md:p-10">
              <p className="kicker !text-beige/55 mb-8">
                {COPY.contact.formNote}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Name" testid="form-name" value={form.name} onChange={update("name")} placeholder="Your name" />
                <Field label="Brand / Business" testid="form-brand" value={form.brand} onChange={update("brand")} placeholder="Company or venue" />
              </div>
              <div className="mt-6">
                <Field
                  label="What would you like to collaborate on?"
                  testid="form-about"
                  value={form.about}
                  onChange={update("about")}
                  placeholder="Restaurant feature, campaign, event…"
                />
              </div>
              <div className="mt-6">
                <Field label="WhatsApp or Email" testid="form-contact" value={form.contact} onChange={update("contact")} placeholder="How can Sona reach you?" />
              </div>

              <button
                data-testid="form-submit"
                onClick={startConversation}
                className="btn-primary !bg-beige !text-ink mt-9 w-full sm:w-auto"
              >
                {COPY.contact.formSubmit}
                <ArrowUpRight size={16} />
              </button>
              <p className="mt-4 text-xs text-beige/45 font-light">
                {COPY.contact.formHint}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.16em] text-beige/55 mb-2">
      {label}
    </span>
    <input
      data-testid={testid}
      type="text"
      className="w-full bg-transparent border-b border-beige/30 focus:border-beige py-2.5 text-beige placeholder:text-beige/30 outline-none transition-colors font-light"
      {...rest}
    />
  </label>
);
